import { z } from "zod";
import { Key } from "./definitions.js";
/** Costs and estimates, step M1 (design 3.10). Money is integer pence; markup is a percentage on cost. */
const Id = z.number().int().positive();
const Label = z.string().min(1).max(200);
const Pence = z.number().int().min(0).max(1_000_000_000_000);
/** a percentage with at most two decimal places, held as basis points */
export const Percent = z.number().min(0).max(1000).refine((n) => Math.abs(n * 100 - Math.round(n * 100)) < 1e-9, "at most two decimal places");
export const FinanceSettings = z.object({ currency: z.string().regex(/^[A-Z]{3}$/) });
export const UpdateFinanceSettingsInput = z.object({ currency: z.string().regex(/^[A-Z]{3}$/, "a three-letter ISO 4217 code such as GBP").optional() }).strict();
export const CostCategoryKind = z.enum(["internal_labour", "external", "other"]);
export const CostCategory = z.object({ id: Id, key: Key, label: Label, kind: CostCategoryKind, sort: z.number().int(), archived: z.boolean() });
export const CreateCostCategoryInput = z.object({ key: Key, label: Label, kind: CostCategoryKind.default("other"), sort: z.number().int().optional() }).strict();
export const UpdateCostCategoryInput = z.object({ label: Label.optional(), sort: z.number().int().optional(), archived: z.boolean().optional() }).strict();
export const RateUnit = z.enum(["hour", "day"]);
/** An internal labour rate for a role, not a person. */
export const RateCard = z.object({ id: Id, key: Key, label: Label, unit: RateUnit, costPence: Pence, archived: z.boolean() });
export const CreateRateCardInput = z.object({ key: Key, label: Label, unit: RateUnit, costPence: Pence }).strict();
export const UpdateRateCardInput = z.object({ label: Label.optional(), costPence: Pence.optional(), archived: z.boolean().optional() }).strict();
export const ProjectStage = z.enum(["proposed", "live", "closed", "declined"]);
export const SetStageInput = z.object({ stage: ProjectStage }).strict();
export const Funding = z.enum(["chargeable", "funded"]);
/** A project's money settings: seen by those who may view its costs. */
export const ProjectFinance = z.object({
    containerId: Id,
    stage: ProjectStage,
    funding: Funding,
    /** chargeable only: price = cost × (1 + markup) */
    markupPercent: z.number().nullable(),
    funderContactId: Id.nullable(),
    customerContactId: Id.nullable(),
    /** what the caller may do here */
    canManage: z.boolean(),
});
export const UpdateProjectFinanceInput = z
    .object({
    funding: Funding.optional(),
    markupPercent: Percent.nullable().optional(),
    funderContactId: Id.nullable().optional(),
    customerContactId: Id.nullable().optional(),
})
    .strict();
// ---------- estimates (step M2) ----------
const IsoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** two decimal places at most, e.g. 2.5 days or 12 units */
export const Quantity = z.number().min(0).max(1_000_000_000).refine((n) => Math.abs(n * 100 - Math.round(n * 100)) < 1e-9, "at most two decimal places");
export const EstimateStatus = z.enum(["draft", "submitted", "accepted", "rejected", "superseded"]);
export const LineRepeat = z.enum(["once", "week", "month", "year"]);
export const EstimateLine = z.object({
    id: Id,
    estimateId: Id,
    sort: z.number().int(),
    categoryId: Id,
    description: z.string(),
    /** a work item in the project; `visible: false` with no name when the caller cannot read it */
    object: z.object({ id: Id, name: z.string().nullable(), visible: z.boolean() }).nullable(),
    contactId: Id.nullable(),
    rateCardId: Id.nullable(),
    quantity: z.number(),
    unit: z.string(),
    unitCostPence: z.number().int(),
    repeat: LineRepeat,
    /** this line's own markup; null takes the estimate's */
    markupPercent: z.number().nullable(),
    /** how many times a repeating line counts over the period (1 for once) */
    repeats: z.number().int(),
    costPence: z.number().int(),
    /** chargeable estimates only */
    pricePence: z.number().int().nullable(),
});
export const EstimateTotals = z.object({
    costPence: z.number().int(),
    pricePence: z.number().int().nullable(),
    profitPence: z.number().int().nullable(),
    byCategory: z.array(z.object({ categoryId: Id, key: z.string(), label: z.string(), costPence: z.number().int(), pricePence: z.number().int().nullable() })),
});
export const Estimate = z.object({
    id: Id,
    containerId: Id,
    number: z.number().int(),
    status: EstimateStatus,
    periodStart: IsoDay.nullable(),
    periodEnd: IsoDay.nullable(),
    /** templates: days from the project's start, and length in days */
    periodOffsetDays: z.number().int().nullable(),
    periodLengthDays: z.number().int().nullable(),
    /** the project's funding while a draft; fixed at submit */
    funding: Funding,
    /** the markup lines take unless they set their own (chargeable only) */
    markupPercent: z.number().nullable(),
    notes: z.string(),
    copiedFromId: Id.nullable(),
    submittedAt: z.coerce.date().nullable(),
    decidedAt: z.coerce.date().nullable(),
    decidedBy: Id.nullable(),
    version: z.number().int(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    totals: EstimateTotals,
    lines: z.array(EstimateLine).optional(),
});
export const CreateEstimateInput = z
    .object({
    /** start from a copy of this estimate of the same project (its lines too) */
    copyFrom: Id.optional(),
    periodStart: IsoDay.nullable().optional(),
    periodEnd: IsoDay.nullable().optional(),
    periodOffsetDays: z.number().int().min(0).nullable().optional(),
    periodLengthDays: z.number().int().min(1).nullable().optional(),
    markupPercent: Percent.nullable().optional(),
    notes: z.string().max(20_000).optional(),
})
    .strict();
export const UpdateEstimateInput = CreateEstimateInput.omit({ copyFrom: true }).extend({ version: z.number().int().optional() }).strict();
export const EstimateLineInput = z
    .object({
    categoryId: Id,
    description: z.string().max(2000).default(""),
    objectId: Id.nullable().optional(),
    contactId: Id.nullable().optional(),
    /** internal labour: the rate card sets unit and unit cost (copied, so later rate changes leave this line alone) */
    rateCardId: Id.nullable().optional(),
    quantity: Quantity,
    unit: z.string().max(40).optional(),
    unitCostPence: Pence.optional(),
    repeat: LineRepeat.default("once"),
    markupPercent: Percent.nullable().optional(),
    sort: z.number().int().optional(),
})
    .strict();
export const UpdateEstimateLineInput = z
    .object({
    categoryId: Id.optional(),
    description: z.string().max(2000).optional(),
    objectId: Id.nullable().optional(),
    contactId: Id.nullable().optional(),
    rateCardId: Id.nullable().optional(),
    quantity: Quantity.optional(),
    unit: z.string().max(40).optional(),
    unitCostPence: Pence.optional(),
    repeat: LineRepeat.optional(),
    markupPercent: Percent.nullable().optional(),
    sort: z.number().int().optional(),
})
    .strict();
// ---------- cost records and the summary (step M3) ----------
export const CostRecordKind = z.enum(["debit", "credit", "income"]);
export const CostRecord = z.object({
    id: Id,
    containerId: Id,
    /** debit adds cost (an invoice), credit reduces it (a credit note), income is a payment received */
    kind: CostRecordKind,
    title: z.string(),
    date: IsoDay,
    description: z.string(),
    /** who the invoice is from, or who paid */
    contactId: Id,
    /** null for income */
    categoryId: Id.nullable(),
    /** a work item in the project; `visible: false` with no name when the caller cannot read it */
    object: z.object({ id: Id, name: z.string().nullable(), visible: z.boolean() }).nullable(),
    netPence: z.number().int(),
    vatPence: z.number().int(),
    reference: z.string(),
    /** where the invoice is held, when it is kept elsewhere */
    invoiceUrl: z.string().nullable(),
    /** files stored on the record (the invoice itself), downloaded from /attachments/{id}/content (design 3.11) */
    attachments: z.array(z.object({ id: Id, name: z.string(), mime: z.string(), size: z.number().int() })),
    version: z.number().int(),
    deleted: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
const Url = z.string().url().max(2000).refine((u) => /^https?:\/\//.test(u), "an http(s) link");
export const CreateCostRecordInput = z
    .object({
    kind: CostRecordKind,
    title: z.string().min(1).max(300),
    date: IsoDay,
    description: z.string().max(20_000).default(""),
    contactId: Id,
    categoryId: Id.nullable().optional(),
    objectId: Id.nullable().optional(),
    netPence: Pence,
    vatPence: Pence.default(0),
    reference: z.string().max(200).default(""),
    invoiceUrl: Url.nullable().optional(),
})
    .strict();
export const UpdateCostRecordInput = z
    .object({
    title: z.string().min(1).max(300).optional(),
    date: IsoDay.optional(),
    description: z.string().max(20_000).optional(),
    contactId: Id.optional(),
    categoryId: Id.nullable().optional(),
    objectId: Id.nullable().optional(),
    netPence: Pence.optional(),
    vatPence: Pence.optional(),
    reference: z.string().max(200).optional(),
    invoiceUrl: Url.nullable().optional(),
    version: z.number().int().optional(),
})
    .strict();
export const ListCostRecordsQuery = z.object({
    kind: CostRecordKind.optional(),
    /** a category id or key */
    category: z.string().optional(),
    from: IsoDay.optional(),
    to: IsoDay.optional(),
    include_deleted: z.enum(["true", "false"]).optional(),
});
/** Budget against actual for a project (design 3.10). All amounts net of VAT unless named VAT. */
export const CostSummary = z.object({
    containerId: Id,
    currency: z.string(),
    funding: Funding,
    /** the accepted estimate the budget comes from, if any */
    estimate: z.object({ id: Id, number: z.number().int() }).nullable(),
    budgetPence: z.number().int(),
    actualPence: z.number().int(),
    remainingPence: z.number().int(),
    costVatPence: z.number().int(),
    incomePence: z.number().int(),
    incomeVatPence: z.number().int(),
    /** chargeable only */
    pricePence: z.number().int().nullable(),
    expectedProfitPence: z.number().int().nullable(),
    profitToDatePence: z.number().int().nullable(),
    byCategory: z.array(z.object({
        categoryId: Id,
        key: z.string(),
        label: z.string(),
        budgetPence: z.number().int(),
        actualPence: z.number().int(),
        /** what is left of the budget, never below zero */
        remainingPence: z.number().int(),
        /** spent beyond the budget */
        overPence: z.number().int(),
    })),
});
//# sourceMappingURL=finance.js.map